import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import communicationApi from '../../../../../services/communicationApi';
import {
    ChannelConfig,
    ChannelType,
    Group,
    MessageComposition,
    ProviderTemplate,
    Routing,
    Template,
} from '../../../../../types/communication';
import {
    cloneTemplateDraft,
    isDraftId,
    newMetaDraft,
    newTemplateDraft,
    suggestTemplateName,
    bodiesOf,
    envelopeOf,
    isEnvelope,
    newEnvelopeDraft,
    renderableContent,
    syncAll,
} from './studioDraft';
import { StudioIssue, StudioSection, computeIssues } from './studioIssues';

export type StudioMode = 'new' | 'view' | 'edit';

interface LoadedState {
    meta: MessageComposition;
    templates: Template[];
}

const errorMessage = (e: unknown, fallback: string): string => (e instanceof Error && e.message ? e.message : fallback);

const SES_BODY_UNAVAILABLE =
    'Não foi possível ler o corpo do e-mail no SES. Recarregue a tela antes de salvar para não gravar um corpo vazio.';

export const useMessageCompositionStudio = (id: string | undefined, mode: StudioMode) => {
    const [loading, setLoading] = useState(mode !== 'new');
    const [saving, setSaving] = useState(false);
    const [meta, setMeta] = useState<MessageComposition | null>(null);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [original, setOriginal] = useState<LoadedState | null>(null);
    const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
    const [section, setSection] = useState<StudioSection>('content');
    const [groups, setGroups] = useState<Group[]>([]);
    const [allTemplates, setAllTemplates] = useState<Template[]>([]);
    const [routings, setRoutings] = useState<Routing[]>([]);
    const [apiVersion, setApiVersion] = useState('');
    const [providerCatalog, setProviderCatalog] = useState<ProviderTemplate[]>([]);
    const [catalogAvailable, setCatalogAvailable] = useState(true);
    const [testValues, setTestValues] = useState<Record<string, string>>({});
    const [dirty, setDirty] = useState(false);

    const readOnly = mode === 'view';

    const applyLoaded = useCallback((loaded: MessageComposition, catalogTemplates: Template[]) => {
        const linked = loaded.templates.length
            ? loaded.templates
            : loaded.templateIds
                  .map((tid) => catalogTemplates.find((t) => t.id === tid))
                  .filter((t): t is Template => Boolean(t));
        const synced = syncAll(linked, loaded.channelConfig);
        setMeta(loaded);
        setTemplates(synced);
        setOriginal({ meta: loaded, templates: synced });
        setActiveTemplateId((current) =>
            current && synced.some((t) => t.id === current) ? current : bodiesOf(synced)[0]?.id ?? synced[0]?.id ?? null,
        );
        setDirty(false);
    }, []);

    const load = useCallback(async () => {
        const catalogFailed = Symbol('catalog-failed');
        const [groupList, templatePage, routingList, catalog] = await Promise.all([
            communicationApi.listGroups({}),
            communicationApi.listTemplates({ pageSize: 9999 }),
            communicationApi.listRoutings({}),
            communicationApi.getProviderCatalog('BLIP').catch(() => catalogFailed),
        ]);
        setGroups(groupList);
        setAllTemplates(templatePage.list);
        setRoutings(routingList);
        if (catalog === catalogFailed) {
            setCatalogAvailable(false);
            setProviderCatalog([]);
            toast.warn('Não foi possível carregar o catálogo de modelos do WhatsApp. A validação de modelo aprovado fica indisponível.');
        } else {
            setCatalogAvailable(true);
            setProviderCatalog(catalog as ProviderTemplate[]);
        }
        setApiVersion(`${templatePage.apiName} - ${templatePage.apiVersion}`);
        if (!id || mode === 'new') return;
        const loaded = await communicationApi.getMessageComposition(id);
        if (loaded.templates?.some((t) => t.contentUnavailable)) toast.warn(SES_BODY_UNAVAILABLE);
        applyLoaded(loaded, templatePage.list);
    }, [id, mode, applyLoaded]);

    useEffect(() => {
        setLoading(true);
        load()
            .catch(() => toast.error('Não foi possível carregar a composição.'))
            .finally(() => setLoading(false));
    }, [load]);

    useEffect(() => {
        if (mode === 'new') {
            setMeta(null);
            setTemplates([]);
            setSection('identity');
        }
    }, [mode]);

    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    const activeTemplate = templates.find((t) => t.id === activeTemplateId) ?? templates[0] ?? null;
    const bodies = bodiesOf(templates);
    const activeBody = activeTemplate && !isEnvelope(activeTemplate) ? activeTemplate : bodies[0] ?? null;
    const envelopeTemplate = envelopeOf(templates) ?? null;

    const previewVariables = useMemo(() => {
        const sources = meta?.channel === 'EMAIL' ? [activeBody, envelopeTemplate] : [activeTemplate];
        const all = sources.flatMap((t) => t?.variables ?? []);
        return all.filter((v, i) => all.findIndex((x) => x.name === v.name) === i);
    }, [meta, activeBody, envelopeTemplate, activeTemplate]);

    const previewValues = useMemo(() => ({ ...testValues }), [testValues]);

    const issues: StudioIssue[] = useMemo(
        () => (meta ? computeIssues(meta, templates, providerCatalog, catalogAvailable) : []),
        [meta, templates, providerCatalog, catalogAvailable],
    );
    const errors = issues.filter((i) => i.level === 'error');

    const touch = () => setDirty(true);

    const start = (channel: ChannelType, source?: { meta: MessageComposition; templates: Template[] }) => {
        const draft = newMetaDraft(channel);
        if (source) {
            const copies = source.templates.map((t) => cloneTemplateDraft(t, `${t.name}_COPIA`));
            setMeta({
                ...draft,
                name: `${source.meta.name} (cópia)`,
                description: source.meta.description,
                groupId: source.meta.groupId,
                channelConfig: JSON.parse(JSON.stringify(source.meta.channelConfig)),
            });
            setTemplates(copies);
            setActiveTemplateId(copies[0]?.id ?? null);
        } else {
            const first = newTemplateDraft(channel, channel === 'EMAIL' ? 'CORPO' : 'PRINCIPAL');
            const initial = channel === 'EMAIL' ? [first, newEnvelopeDraft('ENVIO')] : [first];
            setMeta(draft);
            setTemplates(syncAll(initial, draft.channelConfig));
            setActiveTemplateId(first.id);
        }
        setSection('identity');
        touch();
    };

    const updateMeta = (patch: Partial<MessageComposition>) => {
        if (!meta || readOnly) return;
        setMeta({ ...meta, ...patch });
        touch();
    };

    const updateChannelConfig = (channelConfig: ChannelConfig) => {
        if (!meta || readOnly) return;
        setMeta({ ...meta, channelConfig });
        setTemplates((list) => syncAll(list, channelConfig));
        touch();
    };

    const updateTemplate = (templateId: string, patch: Partial<Template>) => {
        if (!meta || readOnly) return;
        setTemplates((list) =>
            syncAll(
                list.map((t) => (t.id === templateId ? { ...t, ...patch } : t)),
                meta.channelConfig,
            ),
        );
        touch();
    };

    const addTemplate = (template: Template) => {
        if (!meta) return;
        setTemplates((list) => syncAll([...list, template], meta.channelConfig));
        setActiveTemplateId(template.id);
        setSection(isEnvelope(template) ? 'delivery' : 'content');
        touch();
    };

    const draftNewTemplate = () => {
        if (!meta) return;
        addTemplate(newTemplateDraft(meta.channel, suggestTemplateName(meta, bodies)));
    };

    const createEnvelope = () => {
        if (!meta || envelopeTemplate) return;
        addTemplate(newEnvelopeDraft(`${suggestTemplateName(meta, [])}_ENVIO`));
    };

    const duplicateTemplate = (templateId: string) => {
        const source = templates.find((t) => t.id === templateId);
        if (source) addTemplate(cloneTemplateDraft(source, `${source.name}_COPIA`));
    };

    const linkExistingTemplate = async (templateId: string) => {
        const existing = allTemplates.find((t) => t.id === templateId);
        try {
            const full = await communicationApi.getTemplate(templateId);
            addTemplate(full);
        } catch {
            if (existing) {
                addTemplate(existing);
                toast.warn('Não foi possível carregar o conteúdo completo do modelo; revise antes de salvar.');
            } else {
                toast.error('Não foi possível vincular o modelo.');
            }
        }
    };

    const removeTemplate = (templateId: string) => {
        const remaining = templates.filter((t) => t.id !== templateId);
        setTemplates(remaining);
        if (activeTemplateId === templateId) setActiveTemplateId(bodiesOf(remaining)[0]?.id ?? null);
        touch();
    };

    const discard = () => {
        if (!original) return;
        setMeta(original.meta);
        setTemplates(original.templates);
        setActiveTemplateId(original.templates[0]?.id ?? null);
        setDirty(false);
    };

    const studioTemplates = () => {
        const emailProviderRef =
            meta?.channel === 'EMAIL' && meta.channelConfig.kind === 'EMAIL'
                ? meta.channelConfig.adapter.providerTemplateName?.trim()
                : undefined;
        return templates.map((t) => ({
            id: t.id,
            channel: t.channel,
            role: t.role,
            name: t.name,
            content: renderableContent(t),
            subject: t.envelope?.subject,
            providerRef: t.providerTemplateName || (isEnvelope(t) ? undefined : emailProviderRef) || undefined,
            providerBindings: t.providerBindings,
            editorSource:
                t.editorMode && t.blocks ? { editorMode: t.editorMode, blocks: t.blocks } : null,
            variables: t.variables,
        }));
    };

    const save = async (): Promise<MessageComposition | null> => {
        if (!meta) return null;
        if (templates.some((t) => t.contentUnavailable)) {
            toast.error(SES_BODY_UNAVAILABLE);
            return null;
        }
        if (errors.length) {
            toast.warn(`Resolva ${errors.length} pendência(s) antes de salvar.`);
            setSection(errors[0].section);
            return null;
        }
        setSaving(true);
        try {
            const isNew = isDraftId(meta.id);
            let targetUuid = meta.id;
            if (isNew) {
                const created = await communicationApi.createMessageComposition({
                    name: meta.name.trim(),
                    description: meta.description.trim(),
                    channel: meta.channel,
                    groupId: meta.groupId,
                    channelConfig: meta.channelConfig,
                });
                targetUuid = created.id;
                setMeta((m) => (m ? { ...m, id: created.id } : m));
            }
            const revised = await communicationApi.reviseMessageComposition(targetUuid, {
                name: meta.name.trim(),
                description: meta.description.trim(),
                groupId: meta.groupId,
                channelConfig: meta.channelConfig,
                version: isNew ? undefined : meta.version,
                templates: studioTemplates(),
            });
            toast.success(isNew ? 'Composição criada.' : 'Alterações salvas.');
            return await reloadSaved(revised.id);
        } catch (e) {
            toast.error(errorMessage(e, 'Falha ao salvar a composição.'));
            return null;
        } finally {
            setSaving(false);
        }
    };

    const reloadSaved = async (uuid: string): Promise<MessageComposition> => {
        try {
            const saved = await communicationApi.getMessageComposition(uuid);
            applyLoaded(saved, allTemplates);
            return saved;
        } catch {
            setDirty(false);
            toast.warn('Composição salva, mas não foi possível recarregá-la. Recarregue a tela para ver o estado atual.');
            return { ...(meta as MessageComposition), id: uuid };
        }
    };

    const toggleStatus = async (reason: string) => {
        if (!meta) return;
        try {
            const updated = await communicationApi.toggleMessageCompositionStatus(meta.id, reason);
            setMeta({ ...meta, status: updated.status, statusReason: updated.statusReason, updatedAt: updated.updatedAt });
            toast.success(updated.status === 'ACTIVE' ? 'Composição ativada.' : 'Composição desativada.');
        } catch (e) {
            toast.error(errorMessage(e, 'Falha ao alterar o status.'));
        }
    };

    const duplicateMeta = async (): Promise<MessageComposition | null> => {
        if (!meta) return null;
        try {
            const updated = await communicationApi.duplicateMessageComposition(meta.id);
            toast.success('Composição duplicada.');
            return updated;
        } catch (e) {
            toast.error(errorMessage(e, 'Falha ao duplicar.'));
            return null;
        }
    };

    const linkableTemplates = useMemo(
        () =>
            meta
                ? allTemplates.filter(
                      (t) =>
                          t.channel === meta.channel &&
                          !isEnvelope(t) &&
                          !t.messageCompositionId &&
                          !templates.some((x) => x.id === t.id),
                  )
                : [],
        [allTemplates, meta, templates],
    );

    const usedBy = useMemo(
        () => (meta ? routings.filter((r) => r.targets.some((t) => t.messageCompositionId === meta.id)) : []),
        [routings, meta],
    );

    const addGroup = (group: Group) => {
        setGroups((current) => (current.some((g) => g.id === group.id) ? current : [...current, group]));
    };

    return {
        loading,
        saving,
        readOnly,
        apiVersion,
        providerCatalog,
        meta,
        templates,
        activeTemplate,
        activeBody,
        bodies,
        envelopeTemplate,
        previewVariables,
        setActiveTemplateId,
        section,
        setSection,
        groups,
        linkableTemplates,
        usedBy,
        issues,
        errors,
        dirty,
        previewValues,
        testValues,
        setTestValues,
        start,
        updateMeta,
        updateChannelConfig,
        updateTemplate,
        draftNewTemplate,
        createEnvelope,
        duplicateTemplate,
        linkExistingTemplate,
        removeTemplate,
        discard,
        save,
        toggleStatus,
        duplicateMeta,
        addGroup,
        reload: load,
    };
};

export type MessageCompositionStudio = ReturnType<typeof useMessageCompositionStudio>;
